import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Which request of the lesson start failed. Both leave the student with no
 * session behind the screen, so both get the same blocking screen instead of a
 * toast over a player that only looks usable.
 */
export type LessonStartFailureKind = "discovery" | "boot";

export interface LessonStartFailureReport {
  kind: LessonStartFailureKind;
  /**
   * Re-runs the failed step. Returns false when there was nothing to start,
   * so the screen does not wait on a retry that never began.
   */
  retry: () => boolean | void;
}

export interface LessonStartFailure extends LessonStartFailureReport {
  /** A retry is in flight; the screen stays up until its step settles. */
  isRetrying: boolean;
}

/**
 * Holds the failed step of a lesson start and the one way out of it.
 *
 * Input: nothing; failures are reported through `reportFailure`.
 * Output: the current failure, `reportFailure`, `clearFailure` and `retryNow`.
 * Mutates: nothing outside its own state.
 *
 * A failure is retried by the student's click or by the browser's `online`
 * event, whichever comes first; the other finds a retry in flight and does
 * nothing. The screen stays up while the retry runs -- dropping it at the
 * click showed the player, with no session behind its buttons, for the whole
 * request. The step clears it when it settles (`clearFailure`), or reports
 * again if it failed again.
 */
export function useLessonStartFailure() {
  const [failure, setFailure] = useState<LessonStartFailure | null>(null);
  const failureRef = useRef<LessonStartFailure | null>(null);

  const commit = useCallback((next: LessonStartFailure | null) => {
    failureRef.current = next;
    setFailure(next);
  }, []);

  const reportFailure = useCallback(
    (report: LessonStartFailureReport) => {
      commit({ ...report, isRetrying: false });
    },
    [commit],
  );

  const clearFailure = useCallback(
    (kind?: LessonStartFailureKind) => {
      if (kind && failureRef.current?.kind !== kind) return;
      commit(null);
    },
    [commit],
  );

  const retryNow = useCallback(() => {
    const current = failureRef.current;
    if (!current || current.isRetrying) return;
    const retrying = { ...current, isRetrying: true };
    commit(retrying);
    if (current.retry() === false && failureRef.current === retrying) {
      commit(current);
    }
  }, [commit]);

  useEffect(() => {
    if (!failure || failure.isRetrying) return;
    window.addEventListener("online", retryNow);
    return () => window.removeEventListener("online", retryNow);
  }, [failure, retryNow]);

  return { failure, reportFailure, clearFailure, retryNow };
}
